import { localDate } from './format'
import type { ReportEvaluation, ReportSnapshot } from './types'

/** "August 2026" for a `period_month` (or any date in that month). */
export function monthLabel(value: string | Date): string {
  return localDate(value).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

/** `YYYY-MM`, the value an `<input type="month">` wants. */
export function monthInputValue(value: string | Date): string {
  const d = localDate(value)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export function suggestedReportTitle(value: string | Date): string {
  return `${monthLabel(value)} Team Analysis`
}

/**
 * A draft carries `snapshot = {}` — the numbers come from the `report_preview`
 * RPC instead. This is the test for which of the two a report row has, and it
 * narrows the type at the same time.
 */
export function frozenSnapshot(
  snapshot: ReportSnapshot | Record<string, never>,
): ReportSnapshot | null {
  return snapshot && 'period' in snapshot ? (snapshot as ReportSnapshot) : null
}

/** Whether a snapshot has anything but prose in it. */
export function hasData(s: ReportSnapshot | null): boolean {
  return !!s && (s.metrics.length > 0 || s.evaluations.length > 0)
}

/**
 * Per-category movement between an evaluation and the run it's compared against,
 * matched by category NAME. Names are a template's stable identity for its
 * categories (see AGENTS.md on the SWEBOK seed), and two runs of the same
 * template are exactly what `comparison` holds — so a name match is the join.
 */
export function categoryMovement(
  evaluation: ReportEvaluation,
  comparison: ReportEvaluation | null,
): Array<{ name: string; now: number | null; before: number | null; delta: number | null }> {
  const before = new Map(
    (comparison?.categories ?? []).map((c) => [c.name.toLowerCase(), c.average]),
  )
  return evaluation.categories.map((c) => {
    const prior = before.get(c.name.toLowerCase()) ?? null
    return {
      name: c.name,
      now: c.average,
      before: prior,
      delta: c.average != null && prior != null ? c.average - prior : null,
    }
  })
}
